import React, { useEffect, useMemo } from 'react'
import clsx from 'clsx'
import { useFormContext, FieldError, RegisterOptions } from 'react-hook-form'
import { MultiSelectQuestion as MultiSelectQuestionType } from '../../../../types/end-journey'
import { BaseQuestionLayout } from '../BaseQuestionLayout'

interface MultiSelectQuestionProps {
  name: string
  question: MultiSelectQuestionType
  rules?: {
    required?: string | false
  }
}

export const MultiSelectQuestion: React.FC<MultiSelectQuestionProps> = ({
  name,
  question,
  rules,
}) => {
  const {
    register,
    unregister,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext()
  const value = (watch(name) || []) as string[]

  const error = errors[name]?.message as string | undefined
  const otherError = errors[`${question._id}_other`] as FieldError | undefined

  const otherId = useMemo(
    () => question.options.find((v) => v.value.startsWith('other'))?._id || '',
    [question.options]
  )

  const isOtherSelected = useMemo(() => {
    return value.includes(otherId)
  }, [value, otherId])

  const isAtMaxSelection = Boolean(
    question.maxSelectionLimit && value.length >= question.maxSelectionLimit
  )

  // Create validation rules including minimum selection validation
  const validationRules = useMemo(() => {
    const baseRules: RegisterOptions = rules || {}

    if (
      (question.minSelectionLimit && question.minSelectionLimit > 0) ||
      (question.maxSelectionLimit && question.maxSelectionLimit > 0)
    ) {
      return {
        ...baseRules,
        validate: (value: string[]) => {
          if (!value || value.length < question.minSelectionLimit!) {
            return `Please select at least ${question.minSelectionLimit} ${
              question.minSelectionLimit === 1 ? 'theme' : 'themes'
            } to continue.`
          }
          if (!value || value.length > question.maxSelectionLimit!) {
            return `Please select a maximum of ${question.maxSelectionLimit} ${
              question.maxSelectionLimit === 1 ? 'theme' : 'themes'
            } to continue.`
          }
          // Check if other is selected and text is provided
          const otherOptionId =
            question.options.find((v) => v.value.startsWith('other'))?._id || ''
          const otherIsSelected = otherOptionId
            ? value.includes(otherOptionId)
            : false
          const otherTextValue = getValues(`${name}_other`)
          if (otherIsSelected && !otherTextValue) {
            return 'Please specify the "Other" theme'
          }

          return true
        },
      }
    }

    return baseRules
  }, [question.minSelectionLimit, question.maxSelectionLimit, question.options, getValues, name, rules])

  // Manually register the field to properly handle array values
  useEffect(() => {
    register(name, validationRules)
    return () => {
      unregister(name)
    }
  }, [register, unregister, name, validationRules])

  const handleToggle = (optionId: string) => {
    const isCurrentlySelected = value.includes(optionId)

    // If trying to add and already at max, don't allow
    if (!isCurrentlySelected && isAtMaxSelection) {
      return
    }

    const newValue = isCurrentlySelected
      ? value.filter((v) => v !== optionId)
      : [...value, optionId]

    setValue(name, newValue, { shouldValidate: true })

    // Clear other text if "other" is unselected
    if (optionId === otherId && isCurrentlySelected) {
      setValue(`${question._id}_other`, '', { shouldValidate: true })
    }
  }

  return (
    <BaseQuestionLayout question={question} error={error}>
      <div className="space-y-3">
        <div className="flex justify-end">
          <p className="text-sm text-gray-600">
            {value.length}/{question.maxSelectionLimit} selected
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {question.options.map((option, index) => (
            <React.Fragment key={option._id}>
              {index > 0 && <div className="h-px bg-gray-200" />}
              <div>
                <button
                  type="button"
                  onClick={() => handleToggle(option._id)}
                  disabled={isAtMaxSelection && !value.includes(option._id)}
                  className={clsx(
                    'w-full p-4 text-left transition-colors',
                    value.includes(option._id)
                      ? 'bg-[#EBF5FF]'
                      : isAtMaxSelection
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-gray-50'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={clsx(
                        'w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2',
                        value.includes(option._id)
                          ? 'border-[#2D7BE8] bg-[#2D7BE8]'
                          : 'border-gray-300'
                      )}
                    >
                      {value.includes(option._id) && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="text-gray-900 text-sm md:text-base">
                      {option.label}
                    </span>
                  </div>
                </button>
                {option.value.toLowerCase().startsWith('other') &&
                  isOtherSelected && (
                    <div className="px-12 pb-4 bg-[#EBF5FF]">
                      <input
                        type="text"
                        placeholder={question.otherTextPlaceholder}
                        className={clsx(
                          'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2D7BE8] focus:border-[#2D7BE8]',
                          otherError ? 'border-red-500' : 'border-gray-300'
                        )}
                        {...register(`${question._id}_other`, {
                          required: 'Please specify the other theme',
                        })}
                      />
                      {otherError && (
                        <p className="mt-1 text-sm text-red-500">
                          {otherError.message}
                        </p>
                      )}
                    </div>
                  )}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </BaseQuestionLayout>
  )
}
